import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useOpenLiveChat } from '@hub/hooks';
import { useMediaQuery } from '@mui/material';
import { SystemIcon } from '@monorepo/nova-icons';
import { Button, OTP, Stack, Typography, useTheme } from '@monorepo/nova-ui';
import { MAIL_OTP_MAX_RETRIES } from '@monorepo/types';
import { useOtpCountdownPersistence } from './hooks/use-otp-countdown-persistence';

interface Props {
  handleSendMailClick: () => Promise<boolean>;
  handleCheckOtpClick: (otp: string) => void;
  tries: number;
  isLoading: boolean;
  sourceId: string;
  source: string;
  hasInvalidOtpError?: boolean;
}

export const MailOtpStage: FunctionComponent<Props> = ({
  handleCheckOtpClick,
  tries,
  handleSendMailClick,
  isLoading,
  sourceId,
  source,
  hasInvalidOtpError = false,
}) => {
  const [otp, setOtp] = useState<string>('');
  const { cooldown, activateCooldown } = useOtpCountdownPersistence({
    sourceId,
    source: String(source),
    cooldownDuration: 30,
  });

  // Clear OTP when invalid error appears
  useEffect(() => {
    if (hasInvalidOtpError) {
      setOtp('');
    }
  }, [hasInvalidOtpError]);
  const openLiveChat = useOpenLiveChat({ flow: 'otp' });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation(['mailConfirmationNova']);

  const handleSendCode = async () => {
    if (cooldown === 0) {
      const success = await handleSendMailClick();
      if (success) {
        activateCooldown();
      }
      // If sendMail failed, cooldown remains 0 so user can retry immediately
    }
  };

  const isFullCode = useMemo(() => otp?.length === 6, [otp]);

  const handleEnterKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' && isFullCode) {
        handleCheckOtpClick(otp);
      }
    },
    [otp, isFullCode],
  );
  useEffect(() => {
    window.addEventListener('keydown', handleEnterKeyDown);
    return () => {
      window.removeEventListener('keydown', handleEnterKeyDown);
    };
  }, [handleEnterKeyDown]);

  return (
    <Stack gap={5} alignItems={'center'}>
      <Typography variant={'h3'} textAlign={'center'}>
        <Trans
          ns="mailConfirmationNova"
          i18nKey="enter_code_title"
          components={{ br: <br /> }}
        />
      </Typography>
      <Stack textAlign="center">
        <Typography fontWeight={600}>
          {t('mailConfirmationNova:sent_code_to_email')}
        </Typography>
        <Typography fontWeight={'semiBold'}>
          <Trans
            ns="mailConfirmationNova"
            i18nKey="enter_to_confirm"
            components={{ br: <br /> }}
          />
        </Typography>
      </Stack>

      <Stack alignItems={'center'} width="100%" maxWidth="400px" gap={1}>
        <OTP value={otp} onChange={setOtp} length={6} gap={3} />
        {hasInvalidOtpError && (
          <Typography color={theme.palette.error.main}>
            {t('mailConfirmationNova:invalid_otp_error')}
          </Typography>
        )}
        <Typography
          color={theme.palette.error.main}
          hidden={tries === 0 || hasInvalidOtpError}
        >
          {t('mailConfirmationNova:tries_used', {
            tries,
            max: MAIL_OTP_MAX_RETRIES,
          })}
        </Typography>
      </Stack>

      <Typography textAlign={'center'}>
        <Trans
          ns="mailConfirmationNova"
          i18nKey="code_expiry_and_resend"
          components={{ br: <br /> }}
        />
      </Typography>

      <Stack
        direction={isMobile ? 'column-reverse' : 'row'}
        gap={6}
        width={'100%'}
      >
        <Button
          sx={{ width: '100%' }}
          size={'lg'}
          variant={'outlined'}
          onClick={handleSendCode}
          disabled={cooldown > 0 || isLoading}
        >
          {cooldown > 0
            ? t('mailConfirmationNova:resend_in', { seconds: cooldown })
            : t('mailConfirmationNova:send_code_cta')}
        </Button>
        <Button
          sx={{ width: '100%' }}
          size={'lg'}
          variant={'primary'}
          loading={isLoading}
          disabled={!isFullCode || isLoading}
          onClick={() => handleCheckOtpClick(otp)}
          endIcon={
            <SystemIcon
              iconName={'CheckCircle'}
              color={isFullCode ? 'opposite' : 'disabled'}
            />
          }
        >
          {t('mailConfirmationNova:confirm_cta')}
        </Button>
      </Stack>
      <Button
        variant={'naked'}
        sx={{
          color: theme.palette.blue.main,
          fontWeight: theme.typography.fontWeightRegular,
        }}
        onClick={openLiveChat}
      >
        {t('mailConfirmationNova:contact_support_team')}
      </Button>
    </Stack>
  );
};
